// Componente per il form delle unità eroganti

import React, { useState, useEffect } from 'react';
import { ExecutionUnit, Doctor, HealthcareService } from '../../types/executionUnit';
import { Input, TextArea } from '@progress/kendo-react-inputs';
import { Button } from '@progress/kendo-react-buttons';
import { TabStrip, TabStripTab } from '@progress/kendo-react-layout';
import MultiSelectList from './MultiSelectList';
import {
    getExecutionUnitDoctors,
    getExecutionUnitServices,
    getAllAvailableDoctors,
    getAllAvailableServices,
    updateExecutionUnitDoctors,
    updateExecutionUnitServices
} from '../../services/executionUnitService';
import '@progress/kendo-theme-default/dist/all.css';
import './ExecutionUnitForm.css';

interface ExecutionUnitFormProps {
    executionUnit?: ExecutionUnit;
    onSave: (executionUnit: ExecutionUnit) => void;
    onCancel: () => void;
}

const ExecutionUnitForm: React.FC<ExecutionUnitFormProps> = ({ executionUnit, onSave, onCancel }) => {
    const [formData, setFormData] = useState<ExecutionUnit>(executionUnit || {
        id: '',
        name: '',
        code: '',
        description: '',
        createdAt: new Date(),
        updatedAt: new Date()
    });

    // State per associazioni
    const [selectedDoctors, setSelectedDoctors] = useState<Doctor[]>([]);
    const [selectedServices, setSelectedServices] = useState<HealthcareService[]>([]);
    const [availableDoctors, setAvailableDoctors] = useState<Doctor[]>([]);
    const [availableServices, setAvailableServices] = useState<HealthcareService[]>([]);
    const [loadingDoctors, setLoadingDoctors] = useState(false);
    const [loadingServices, setLoadingServices] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    // Carica dati associazioni quando si modifica UE esistente
    useEffect(() => {
        if (executionUnit?.id) {
            loadAssociations();
        }
        loadAvailableData();
    }, [executionUnit]);

    const loadAssociations = async () => {
        if (!executionUnit?.id) return;

        setLoadingDoctors(true);
        setLoadingServices(true);

        try {
            const [doctors, services] = await Promise.all([
                getExecutionUnitDoctors(executionUnit.id),
                getExecutionUnitServices(executionUnit.id)
            ]);
            
            setSelectedDoctors(doctors);
            setSelectedServices(services);
        } catch (error) {
            console.error('Errore durante il caricamento delle associazioni:', error);
        } finally {
            setLoadingDoctors(false);
            setLoadingServices(false);
        }
    };

    const loadAvailableData = async () => {
        try {
            const [doctors, services] = await Promise.all([
                getAllAvailableDoctors(),
                getAllAvailableServices()
            ]);
            
            setAvailableDoctors(doctors);
            setAvailableServices(services);
        } catch (error) {
            console.error('Errore durante il caricamento dei dati disponibili:', error);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleDoctorsChange = async (doctors: Doctor[]) => {
        setSelectedDoctors(doctors);
        
        // Se stiamo modificando una UE esistente, salva immediatamente
        if (executionUnit?.id) {
            try {
                await updateExecutionUnitDoctors(
                    executionUnit.id, 
                    doctors.map(d => d.id)
                );
            } catch (error) {
                console.error('Errore durante l\'aggiornamento dei medici:', error);
            }
        }
    };

    const handleServicesChange = async (services: HealthcareService[]) => {
        setSelectedServices(services);
        
        // Se stiamo modificando una UE esistente, salva immediatamente
        if (executionUnit?.id) {
            try {
                await updateExecutionUnitServices(
                    executionUnit.id, 
                    services.map(s => s.id)
                );
            } catch (error) {
                console.error('Errore durante l\'aggiornamento dei servizi:', error);
            }
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        // Include le associazioni nei dati salvati
        const updatedFormData = {
            ...formData,
            doctors: selectedDoctors,
            services: selectedServices,
            doctorCount: selectedDoctors.length,
            serviceCount: selectedServices.length
        };
        onSave(updatedFormData);
    };

    return (
        <div className="execution-unit-form">
            <h2>{executionUnit ? 'Modifica Unità Erogante' : 'Nuova Unità Erogante'}</h2>
            
            <TabStrip
                selected={activeTab}
                onSelect={(e) => setActiveTab(e.selected)}
                className="execution-unit-tabs"
            >
                <TabStripTab title="Informazioni Base">
                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label htmlFor="name">Nome</label>
                            <Input
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={(e) => setFormData(prev => ({ ...prev, name: e.value }))}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="code">Codice</label>
                            <Input
                                id="code"
                                name="code"
                                value={formData.code}
                                onChange={(e) => setFormData(prev => ({ ...prev, code: e.value }))}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="description">Descrizione</label>
                            <TextArea
                                id="description"
                                name="description"
                                value={formData.description}
                                onChange={(e) => setFormData(prev => ({ ...prev, description: e.value }))}
                                rows={4}
                            />
                        </div>
                        <div className="form-actions">
                            <Button type="submit" themeColor="primary">
                                {executionUnit ? 'Aggiorna Informazioni Base' : 'Crea Unità Erogante'}
                            </Button>
                            <Button type="button" onClick={onCancel}>Annulla</Button>
                        </div>
                    </form>
                </TabStripTab>

                <TabStripTab 
                    title={`Medici Associati (${selectedDoctors.length})`}
                    disabled={!executionUnit?.id}
                >
                    <div style={{ padding: '20px' }}>
                        {!executionUnit?.id ? (
                            <div className="info-message">
                                <p>⚠️ Salva prima le informazioni base per poter associare i medici.</p>
                            </div>
                        ) : (
                            <MultiSelectList
                                title="Medici"
                                selectedItems={selectedDoctors.map(doctor => ({
                                    id: doctor.id,
                                    name: `${doctor.first_name} ${doctor.last_name}`,
                                    code: doctor.code,
                                    description: doctor.specialization
                                }))}
                                availableItems={availableDoctors.map(doctor => ({
                                    id: doctor.id,
                                    name: `${doctor.first_name} ${doctor.last_name}`,
                                    code: doctor.code,
                                    description: doctor.specialization
                                }))}
                                onSelectionChange={(selectedItems) => {
                                    const selectedDoctorsList = selectedItems.map(item => 
                                        availableDoctors.find(d => d.id === item.id)!
                                    );
                                    handleDoctorsChange(selectedDoctorsList);
                                }}
                                loading={loadingDoctors}
                                placeholder="Seleziona medici da associare..."
                            />
                        )}
                    </div>
                </TabStripTab>

                <TabStripTab 
                    title={`Servizi Sanitari (${selectedServices.length})`}
                    disabled={!executionUnit?.id}
                >
                    <div style={{ padding: '20px' }}>
                        {!executionUnit?.id ? (
                            <div className="info-message">
                                <p>⚠️ Salva prima le informazioni base per poter associare i servizi sanitari.</p>
                            </div>
                        ) : (
                            <MultiSelectList
                                title="Servizi Sanitari"
                                selectedItems={selectedServices.map(service => ({
                                    id: service.id,
                                    name: service.name,
                                    code: service.code,
                                    description: service.description || service.category
                                }))}
                                availableItems={availableServices.map(service => ({
                                    id: service.id,
                                    name: service.name,
                                    code: service.code,
                                    description: service.description || service.category
                                }))}
                                onSelectionChange={(selectedItems) => {
                                    const selectedServicesList = selectedItems.map(item => 
                                        availableServices.find(s => s.id === item.id)!
                                    );
                                    handleServicesChange(selectedServicesList);
                                }}
                                loading={loadingServices}
                                placeholder="Seleziona servizi sanitari da erogare..."
                            />
                        )}
                    </div>
                </TabStripTab>
            </TabStrip>
        </div>
    );
};

export default ExecutionUnitForm;
